import { useQueryEntitlements } from '@/hooks/query/useQueryEntitlements';
import { type EntitlementId } from '@heidi/workspace/contracts';

/**
 * Returns whether the current user has an entitlement enabled.
 *
 * Mirrors the feature-flag pattern (`useBooleanFlagValue`) for access
 * entitlements: callers pass a typed ID from the `ENTITLEMENT` catalog and
 * get back a boolean defaulting to `false` while loading or for unauthed
 * users. The underlying `useQueryEntitlements` cache is shared across all
 * callers, so this hook is safe to invoke per call site without fan-out.
 *
 * For dynamic IDs sourced from a backend payload (where the ID isn't known
 * at compile time), keep using `useQueryEntitlements([name])` directly.
 */
export function useIsEntitlementEnabled(entitlement: EntitlementId): {
  isEnabled: boolean;
  isPending: boolean;
} {
  const { data, isPending } = useQueryEntitlements([entitlement]);
  return {
    isEnabled: data?.get(entitlement)?.enabled ?? false,
    isPending,
  };
}
